// ============================================================================
// BudgetService.js - Unified Budget Data Service Layer
// Location: src/services/BudgetService.js
// ============================================================================

import axios from 'axios';
import { API_BASE_URL } from '../api/axiosConfig';

const API_BASE = `${API_BASE_URL}/api`;

class BudgetService {
  constructor() {
    this.token = null;
  }

  setToken(token) {
    this.token = token;
  }

  getHeaders() {
    return {
      Authorization: `Bearer ${this.token || localStorage.getItem('token')}`
    };
  }

  // ===== BUDGET LIMITS (budgets table) =====
  
  async getAllBudgets() {
    try {
      const response = await axios.get(`${API_BASE}/budgets`, {
        headers: this.getHeaders()
      });
      return this.normalizeBudgets(response.data || []);
    } catch (error) {
      console.warn('Could not fetch budgets from backend, defaulting to empty list:', error.message);
      return [];
    }
  }

  async getBudgetById(budgetId) {
    try {
      const response = await axios.get(`${API_BASE}/budgets/${budgetId}`, {
        headers: this.getHeaders()
      });
      const data = response.data?.data || response.data;
      return this.normalizeBudget(data);
    } catch (error) {
      console.warn('Could not fetch budget by id:', error.message);
      return null;
    }
  }

  async getBudgetByProject(projectId) {
    try {
      const response = await axios.get(`${API_BASE}/budgets/project/${projectId}`, {
        headers: this.getHeaders()
      });
      const data = response.data?.data || response.data;
      return this.normalizeBudget(data);
    } catch (error) {
      console.warn('Could not fetch budget for project:', error.message);
      return null;
    }
  }

  async createBudget(budgetData) {
    try {
      const response = await axios.post(`${API_BASE}/budgets`, budgetData, {
        headers: this.getHeaders()
      });
      return this.normalizeBudget(response.data);
    } catch (error) {
      console.error('Error creating budget:', error);
      throw error;
    }
  }

  async updateBudget(budgetId, budgetData) {
    try {
      const response = await axios.put(`${API_BASE}/budgets/${budgetId}`, budgetData, {
        headers: this.getHeaders()
      });
      return this.normalizeBudget(response.data);
    } catch (error) {
      console.error('Error updating budget:', error);
      throw error;
    }
  }

  async deleteBudget(budgetId) {
    try {
      await axios.delete(`${API_BASE}/budgets/${budgetId}`, {
        headers: this.getHeaders()
      });
      return true;
    } catch (error) {
      console.error('Error deleting budget:', error);
      throw error;
    }
  }

  // ===== BUDGET VS ACTUAL ANALYSIS =====
  
  async getBudgetVsActual(projectId = null) {
    try {
      const endpoint = projectId 
        ? `${API_BASE}/budgets/analysis/project/${projectId}`
        : `${API_BASE}/budgets/analysis/all`;
      
      const response = await axios.get(endpoint, {
        headers: this.getHeaders()
      });
      
      return this.normalizeBudgetVsActual(response.data || []);
    } catch (error) {
      console.warn('Could not fetch budget vs actual:', error.message);
      return [];
    }
  }

  // ===== DATA NORMALIZATION =====
  
  normalizeBudget(budget) {
    if (!budget) return null;
    
    return {
      id: budget.id,
      projectId: budget.project_id || budget.projectId,
      projectName: budget.project_name || budget.projectName || 'Unknown Project',
      clientName: budget.client_name || budget.clientName || '-',
      
      // Budget limits
      laborCostEstimate: Number(budget.labor_cost_estimate || budget.laborCostEstimate || 0),
      materialCostEstimate: Number(budget.material_cost_estimate || budget.materialCostEstimate || 0),
      equipmentCostEstimate: Number(budget.equipment_cost_estimate || budget.equipmentCostEstimate || 0),
      realCostEstimate: Number(budget.real_cost_estimate || budget.realCostEstimate || budget.operational_budget || 0),
      
      // Calculated total budget
      totalBudget: Number(budget.total_budget || budget.totalBudget || 
        (Number(budget.labor_cost_estimate || 0) + 
         Number(budget.material_cost_estimate || 0) + 
         Number(budget.equipment_cost_estimate || 0) + 
         Number(budget.real_cost_estimate || budget.operational_budget || 0))),
      
      // Actual spending (if included from JOIN)
      totalSpent: Number(budget.total_spent || budget.totalSpent || 0),
      laborSpent: Number(budget.labor_spent || budget.laborSpent || 0),
      materialSpent: Number(budget.material_spent || budget.materialSpent || 0),
      equipmentSpent: Number(budget.equipment_spent || budget.equipmentSpent || 0),
      operationalSpent: Number(budget.operational_spent || budget.operationalSpent || 0),
      
      // Metadata
      startDate: budget.start_date || budget.startDate,
      endDate: budget.end_date || budget.endDate,
      status: budget.status || 'Planned',
      notes: budget.notes || '',
      
      createdAt: budget.created_at || budget.createdAt,
      updatedAt: budget.updated_at || budget.updatedAt
    };
  }

  normalizeBudgets(budgets) {
    if (!Array.isArray(budgets)) return [];
    return budgets.map(b => this.normalizeBudget(b));
  }

  normalizeBudgetVsActual(data) {
    if (!data) return null;
    
    if (Array.isArray(data)) {
      return data.map(item => this.normalizeSingleBudgetVsActual(item));
    }
    
    return this.normalizeSingleBudgetVsActual(data);
  }

  normalizeSingleBudgetVsActual(item) {
    const totalBudget = Number(item.total_budget || item.totalBudget || 0);
    const totalSpent = Number(item.total_spent || item.totalSpent || 0);
    const variance = totalBudget - totalSpent;
    const variancePercent = totalBudget > 0 ? ((variance / totalBudget) * 100) : 0;
    
    return {
      projectId: item.project_id || item.projectId,
      projectName: item.project_name || item.projectName || 'Unknown',
      clientName: item.client_name || item.clientName || '-',
      
      // Budget breakdown
      laborBudget: Number(item.labor_budget || item.laborBudget || 0),
      materialBudget: Number(item.material_budget || item.materialBudget || 0),
      equipmentBudget: Number(item.equipment_budget || item.equipmentBudget || 0),
      operationalBudget: Number(item.operational_budget || item.operationalBudget || 0),
      totalBudget,
      
      // Actual spending breakdown
      laborSpent: Number(item.labor_spent || item.laborSpent || 0),
      materialSpent: Number(item.material_spent || item.materialSpent || 0),
      equipmentSpent: Number(item.equipment_spent || item.equipmentSpent || 0),
      operationalSpent: Number(item.operational_spent || item.operationalSpent || 0),
      totalSpent,
      
      // Variance analysis
      variance,
      variancePercent,
      status: this.calculateBudgetStatus(variancePercent),
      
      // Metadata
      startDate: item.start_date || item.startDate,
      endDate: item.end_date || item.endDate,
      budgetStatus: item.budget_status || item.status || 'Active'
    };
  }

  calculateBudgetStatus(variancePercent) {
    if (variancePercent < -10) return 'Over Budget';
    if (variancePercent < 0) return 'Critical';
    if (variancePercent < 10) return 'On Track';
    return 'Under Budget';
  }
}

export default new BudgetService();